import { c as _c } from "react/compiler-runtime";
import { IcoArrowRightSmall16, IcoChevronLeft16, IcoChevronRight16, IcoPencil16 } from '@onefootprint/icons';
import { Button, IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { SendMessageArgs, UserMessage } from '../../hooks/use-chat-ws';
import { useHoverState } from '../message-actions';
import PinnedCard, { PinnedBody } from '../pinned-card';
import UserMessageActions from '../user-message-actions';
export type AskUserQuestionInput = {
  question: string;
  header?: string;
  options?: {
    label: string;
    description: string;
  }[];
  multiSelect?: boolean;
  allowOther?: boolean;
};
type AskUserQuestionFlowProps = {
  questions: AskUserQuestionInput[];
  onSendMessage?: (content: string, {
    files,
    questionId
  }: SendMessageArgs) => void;
  questionId: string;
  questionAnswerMessages: Record<string, UserMessage>;
  answered?: boolean;
  prefilledAnswers?: string[];
  /** When true, there's a more recent message than this set of questions. Collapse the answers. */
  disabled?: boolean;
};
type QuestionAnswer = {
  /** Selected option labels (length 0 or 1 for single-select, 0..N for multi-select). */
  selected: string[];
  /** Free-text contents (for free-text questions, or the "Other" textarea). */
  text: string;
  /** Whether the "Other" option is staged for this question. */
  otherSelected: boolean;
  /** Whether the user explicitly skipped this question. Sends as `[No preference]` (matches Claude). */
  skipped: boolean;
};
const SKIPPED_ANSWER = '[No preference]';
const emptyAnswer = (): QuestionAnswer => ({
  selected: [],
  text: '',
  otherSelected: false,
  skipped: false
});
const formatAnswer = (q: AskUserQuestionInput, a: QuestionAnswer): string => {
  if (a.skipped) return SKIPPED_ANSWER;
  if (!q.options || q.options.length === 0) return a.text.trim();
  const parts = [...a.selected];
  if (a.otherSelected && a.text.trim()) parts.push(a.text.trim());
  return parts.join(', ');
};
const isAnswered = (q: AskUserQuestionInput, a: QuestionAnswer): boolean => formatAnswer(q, a).length > 0;

/**
 * A real option can be answered with a single click — and therefore send immediately, like
 * a chat quick reply — when one click fully answers the whole turn: exactly one question,
 * single-select, with options. (Picking "Something else" instead stages a typed answer and
 * shows the Submit footer, even for these.)
 */
const isImmediate = (questions: AskUserQuestionInput[]): boolean => {
  if (questions.length !== 1) return false;
  const q = questions[0];
  return !!q.options && q.options.length > 0 && !q.multiSelect;
};
const CheckGlyph = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true"><path d="M1 4L3.5 6.5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
type OptionRowProps = {
  /** Content of the leading chip — a 1-based number, or an icon for the "Other" row. */
  chip: ReactNode;
  label: string;
  description?: string;
  selected: boolean;
  /** Dimmed while another row ("Something else") is being typed into; un-dims on hover. */
  dimmed?: boolean;
  /** Suppress the trailing hover arrow (multi-select rows don't need it — the
   *  checkbox + footer convey the interaction). */
  noTrailingArrow?: boolean;
  /** 20x20 chip (checkbox-style for multi-select). Default is 24x24 — used by
   *  numbered single-select chips and the pencil "Something else" chip. */
  smallChip?: boolean;
  onClick: () => void;
  onMouseEnter?: () => void;
};

// One row of the question card: leading chip + label + a chevron that reveals on
// hover/selection (à la Claude's option list). With a description, the row grows
// to auto-height and the chip top-aligns with the label.
const OptionRow = t0 => {
  const $ = _c(28);
  const {
    chip,
    label,
    description,
    selected,
    dimmed,
    noTrailingArrow,
    smallChip,
    onClick,
    onMouseEnter
  } = t0;
  const t1 = description ? "items-start py-2" : "items-center min-h-9 max-md:py-1.5 md:h-9";
  const t2 = selected ? "bg-senary/70" : "hover:bg-hover";
  const t3 = dimmed && "opacity-40 hover:opacity-100";
  let t4;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3) {
    t4 = cx("group/opt flex gap-2.5 w-full text-left rounded-lg px-2 transition-all cursor-pointer", t1, t2, t3);
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = smallChip ? "w-5 h-5" : "w-6 h-6";
  const t6 = selected ? "bg-accent/10 border-accent text-accent" : "bg-primary border-tertiary text-tertiary";
  let t7;
  if ($[4] !== t5 || $[5] !== t6) {
    t7 = cx("shrink-0 flex items-center justify-center rounded-md border text-caption-2 font-medium transition-colors", t5, t6);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== chip || $[8] !== t7) {
    t8 = <span className={t7}>{chip}</span>;
    $[7] = chip;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== label) {
    t9 = <span className="text-body-3 text-primary leading-6">{label}</span>;
    $[10] = label;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== description) {
    t10 = description && <span className="text-caption-2 text-tertiary leading-4">{description}</span>;
    $[12] = description;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = <span className="flex-1 min-w-0 flex flex-col gap-1">{t9}{t10}</span>;
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== description || $[18] !== noTrailingArrow) {
    t12 = !noTrailingArrow && <IcoArrowRightSmall16 className={cx("shrink-0 text-tertiary opacity-0 group-hover/opt:opacity-100 transition-opacity", description && "mt-1")} />;
    $[17] = description;
    $[18] = noTrailingArrow;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== onClick || $[21] !== onMouseEnter || $[22] !== selected || $[23] !== t11 || $[24] !== t12 || $[25] !== t4 || $[26] !== t8) {
    t13 = <button type="button" aria-pressed={selected} onClick={onClick} onMouseEnter={onMouseEnter} className={t4}>{t8}{t11}{t12}</button>;
    $[20] = onClick;
    $[21] = onMouseEnter;
    $[22] = selected;
    $[23] = t11;
    $[24] = t12;
    $[25] = t4;
    $[26] = t8;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  return t13;
};
const AskUserQuestionFlow = t0 => {
  const $ = _c(78);
  const {
    questions,
    onSendMessage,
    questionAnswerMessages,
    questionId,
    disabled
  } = t0;
  let t1;
  if ($[0] !== questions) {
    t1 = () => questions.map(emptyAnswer);
    $[0] = questions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [answers, setAnswers] = useState(t1);
  const [submitted, setSubmitted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    hovered: answerHovered,
    handlers: answerHoverBind
  } = useHoverState();
  const answerMessage = questionAnswerMessages[questionId];
  const chosenAnswer = answerMessage?.content?.split("\n").map(_temp);
  let t2;
  if ($[2] !== questions) {
    t2 = isImmediate(questions);
    $[2] = questions;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const immediate = t2;
  const paginated = questions.length > 1;
  const isLast = currentIndex >= questions.length - 1;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (index, updater) => {
      setAnswers(prev => prev.map((a, i) => i === index ? updater(a) : a));
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const updateAnswer = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (index_0, label) => {
      updateAnswer(index_0, a_0 => ({
        ...a_0,
        selected: a_0.selected[0] === label ? [] : [label],
        otherSelected: false,
        skipped: false
      }));
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectSingle = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = (index_1, label_0) => {
      updateAnswer(index_1, a_1 => ({
        ...a_1,
        selected: a_1.selected.includes(label_0) ? a_1.selected.filter(l => l !== label_0) : [...a_1.selected, label_0],
        skipped: false
      }));
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const toggleMulti = t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = (index_2, value) => {
      updateAnswer(index_2, a_2 => ({
        ...a_2,
        text: value,
        skipped: false
      }));
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const setText = t6;
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = (index_3, q) => {
      updateAnswer(index_3, a_3 => {
        const next = !a_3.otherSelected;
        const selected = !q.multiSelect && next ? [] : a_3.selected;
        return {
          ...a_3,
          otherSelected: next,
          selected,
          skipped: false
        };
      });
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const toggleOther = t7;
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = index_4 => updateAnswer(index_4, _temp2);
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const clearOther = t8;
  let t9;
  if ($[10] !== answers || $[11] !== questions) {
    let t10;
    if ($[13] !== answers) {
      t10 = (q_0, i_0) => isAnswered(q_0, answers[i_0] ?? emptyAnswer());
      $[13] = answers;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t9 = questions.every(t10);
    $[10] = answers;
    $[11] = questions;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const allAnswered = t9;
  const t10 = questions[currentIndex];
  let t11;
  if ($[15] !== answers || $[16] !== currentIndex) {
    t11 = answers[currentIndex] ?? emptyAnswer();
    $[15] = answers;
    $[16] = currentIndex;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t10 || $[19] !== t11) {
    t12 = isAnswered(t10, t11);
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const currentAnswered = t12;
  let t13;
  if ($[21] !== questions) {
    t13 = questions.some(_temp3);
    $[21] = questions;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const hasAnyOptions = t13;
  let t14;
  if ($[23] !== onSendMessage || $[24] !== questionId || $[25] !== questions) {
    t14 = responses => {
      const response = questions.map((q_2, i_1) => `${q_2.header ?? `Q${i_1 + 1}`}: ${responses[i_1]}`).join("\n");
      setSubmitted(true);
      onSendMessage?.(response, {
        questionId
      });
    };
    $[23] = onSendMessage;
    $[24] = questionId;
    $[25] = questions;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const send = t14;
  let t15;
  if ($[27] !== answers || $[28] !== questions || $[29] !== send) {
    t15 = () => send(questions.map((q_3, i_2) => formatAnswer(q_3, answers[i_2])));
    $[27] = answers;
    $[28] = questions;
    $[29] = send;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  const handleSubmit = t15;
  let t16;
  if ($[31] !== send) {
    t16 = label_1 => send([label_1]);
    $[31] = send;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const handleImmediate = t16;
  let t17;
  if ($[33] !== handleSubmit || $[34] !== isLast || $[35] !== questions.length) {
    t17 = () => {
      if (isLast) {
        handleSubmit();
      } else {
        setCurrentIndex(i_3 => Math.min(i_3 + 1, questions.length - 1));
      }
    };
    $[33] = handleSubmit;
    $[34] = isLast;
    $[35] = questions.length;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const advance = t17;
  let t18;
  if ($[37] !== currentIndex) {
    t18 = () => {
      if (currentIndex === 0) {
        return;
      }
      const target = currentIndex - 1;
      setAnswers(prev_0 => prev_0.map((a_5, idx) => idx === target ? emptyAnswer() : a_5));
      setCurrentIndex(target);
    };
    $[37] = currentIndex;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  const goPrev = t18;
  let t19;
  if ($[39] !== answers) {
    t19 = answers.some(_temp4);
    $[39] = answers;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  const anyOtherSelected = t19;
  const cardRef = useRef(null);
  let t20;
  let t21;
  if ($[41] !== anyOtherSelected) {
    t20 = () => {
      if (!anyOtherSelected) {
        return;
      }
      const onDown = e => {
        if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
          setAnswers(_temp6);
        }
      };
      document.addEventListener("mousedown", onDown);
      return () => document.removeEventListener("mousedown", onDown);
    };
    t21 = [anyOtherSelected];
    $[41] = anyOtherSelected;
    $[42] = t20;
    $[43] = t21;
  } else {
    t20 = $[42];
    t21 = $[43];
  }
  useEffect(t20, t21);
  if (submitted || answerMessage || disabled) {
    const displayAnswers = submitted ? answers.map((a_8, i_4) => formatAnswer(questions[i_4], a_8)) : chosenAnswer;
    const pairs = questions.map((q_4, i_5) => ({
      q: q_4.question,
      a: displayAnswers?.[i_5]?.trim()
    })).filter(_temp7);
    if (pairs.length === 0) {
      return null;
    }
    return <div className="flex flex-col items-end gap-1" {...answerHoverBind}><div className="bg-secondary rounded-2xl px-4 py-2 max-w-[80%] flex flex-col gap-3 text-body-2">{pairs.map(_temp8)}</div><UserMessageActions content={pairs.map(_temp9).join("\n\n")} visible={answerHovered} /></div>;
  }
  let t22;
  if ($[44] !== advance || $[45] !== allAnswered || $[46] !== answers || $[47] !== clearOther || $[48] !== currentAnswered || $[49] !== currentIndex || $[50] !== goPrev || $[51] !== handleImmediate || $[52] !== handleSubmit || $[53] !== hasAnyOptions || $[54] !== immediate || $[55] !== isLast || $[56] !== paginated || $[57] !== questions || $[58] !== selectSingle || $[59] !== send || $[60] !== setText || $[61] !== toggleMulti || $[62] !== toggleOther) {
    t22 = (q_5, i_6) => {
      const a_9 = answers[i_6] ?? emptyAnswer();
      const onSkipClick = () => {
        const nextAnswer = {
          selected: [],
          text: "",
          otherSelected: false,
          skipped: true
        };
        const nextAnswers = answers.map((ans, idx_0) => idx_0 === i_6 ? nextAnswer : ans);
        setAnswers(nextAnswers);
        if (paginated) {
          if (isLast) {
            send(nextAnswers.map((ans_0, idx_1) => formatAnswer(questions[idx_1], ans_0)));
          } else {
            setCurrentIndex(idx_2 => Math.min(idx_2 + 1, questions.length - 1));
          }
        } else {
          send(nextAnswers.map((ans_1, idx_3) => formatAnswer(questions[idx_3], ans_1)));
        }
      };
      const onOptionClick = label_2 => {
        if (paginated) {
          const prev_2 = answers[i_6] ?? emptyAnswer();
          const nextAnswer_0 = q_5.multiSelect ? {
            ...prev_2,
            selected: prev_2.selected.includes(label_2) ? prev_2.selected.filter(l_0 => l_0 !== label_2) : [...prev_2.selected, label_2],
            skipped: false
          } : {
            ...prev_2,
            selected: prev_2.selected[0] === label_2 ? [] : [label_2],
            otherSelected: false,
            skipped: false
          };
          const nextAnswers_0 = answers.map((a_10, idx_4) => idx_4 === i_6 ? nextAnswer_0 : a_10);
          setAnswers(nextAnswers_0);
          if (q_5.multiSelect) {
            return;
          }
          if (isLast) {
            send(nextAnswers_0.map((a_11, idx_5) => formatAnswer(questions[idx_5], a_11)));
          } else {
            setCurrentIndex(idx_6 => Math.min(idx_6 + 1, questions.length - 1));
          }
        } else {
          if (immediate) {
            handleImmediate(label_2);
          } else {
            if (q_5.multiSelect) {
              toggleMulti(i_6, label_2);
            } else {
              selectSingle(i_6, label_2);
            }
          }
        }
      };
      const skipButton = <button type="button" onClick={onSkipClick} className={cx("shrink-0 text-caption-1 text-tertiary hover:text-primary transition-colors cursor-pointer max-md:py-2", !paginated && "max-md:-my-2")}>Skip</button>;
      return <div key={q_5.question} className="flex flex-col gap-2.5 min-h-0">{paginated ? <div className="flex flex-col-reverse gap-1 px-2 md:flex-row md:items-start md:justify-between md:gap-3"><p className="text-label-2 text-primary md:flex-1 min-w-0">{q_5.question}</p><div className="shrink-0 flex items-center gap-2 text-caption-1 text-tertiary max-md:self-end">{skipButton}<div className="flex items-center gap-1"><IconButton aria-label="Previous question" disabled={currentIndex === 0} onClick={goPrev} className="max-md:size-8" size="tiny" icon={IcoChevronLeft16} color="tertiary" /><span className="tabular-nums px-1 text-caption-1">{currentIndex + 1} of {questions.length}</span><IconButton aria-label="Next question" onClick={advance} disabled={!currentAnswered} className="max-md:size-8" size="tiny" icon={IcoChevronRight16} color="tertiary" /></div></div></div> : <div className="flex items-start justify-between gap-3 px-2"><p className="text-label-2 text-primary flex-1 min-w-0">{q_5.question}</p>{skipButton}</div>}<PinnedBody itemCount={(q_5.options?.length ?? 0) + 1} noFade={a_9.otherSelected} className="flex flex-col gap-0.5">{q_5.options?.map((opt, idx_7) => {
            const selected_0 = a_9.selected.includes(opt.label);
            const chip = q_5.multiSelect ? selected_0 ? <CheckGlyph /> : null : idx_7 + 1;
            return <OptionRow key={opt.label} chip={chip} label={opt.label} description={opt.description} selected={selected_0} dimmed={!q_5.multiSelect && a_9.otherSelected} noTrailingArrow={q_5.multiSelect} smallChip={q_5.multiSelect} onMouseEnter={!q_5.multiSelect && a_9.otherSelected ? () => clearOther(i_6) : undefined} onClick={() => onOptionClick(opt.label)} />;
          })}{a_9.otherSelected ? <div className="flex items-center gap-2.5 rounded-lg pl-2 pr-1.5 h-9 bg-secondary"><button type="button" aria-label={q_5.multiSelect ? "Uncheck Something else" : "Cancel typing"} onClick={() => toggleOther(i_6, q_5)} className={cx("shrink-0 flex items-center justify-center rounded-md border bg-accent/10 border-accent text-accent cursor-pointer", q_5.multiSelect ? "w-5 h-5" : "w-6 h-6")}>{q_5.multiSelect ? <CheckGlyph /> : <IcoPencil16 className="scale-[0.85]" />}</button><input autoFocus={true} type="text" className="flex-1 min-w-0 bg-transparent text-body-3 text-primary focus:outline-none placeholder:text-tertiary" placeholder="Type your answer..." value={a_9.text} onChange={e_0 => setText(i_6, e_0.target.value)} onKeyDown={e_1 => {
              if (e_1.key !== "Enter" || e_1.shiftKey) {
                return;
              }
              if (paginated) {
                if (currentAnswered) {
                  e_1.preventDefault();
                  advance();
                }
              } else {
                if (allAnswered) {
                  e_1.preventDefault();
                  handleSubmit();
                }
              }
            }} />{!q_5.multiSelect && (immediate || paginated || !hasAnyOptions) && <button type="button" aria-label={paginated && !isLast ? "Next" : "Submit"} disabled={paginated ? !currentAnswered : !allAnswered} onClick={paginated ? advance : handleSubmit} className={cx("shrink-0 flex items-center justify-center rounded-full size-8 md:size-7 transition-colors", (paginated ? currentAnswered : allAnswered) ? "bg-neutral-inverted cursor-pointer" : "bg-senary opacity-50")}><IcoArrowRightSmall16 color={(paginated ? currentAnswered : allAnswered) ? "quinary" : undefined} /></button>}</div> : <OptionRow chip={q_5.multiSelect ? null : <IcoPencil16 className="scale-[0.85]" />} label="Something else" selected={false} noTrailingArrow={q_5.multiSelect} smallChip={q_5.multiSelect} onClick={() => toggleOther(i_6, q_5)} />}</PinnedBody>{paginated && q_5.multiSelect && (q_5.options?.length ?? 0) > 0 && <div className="flex items-center justify-between border-t border-tertiary pt-3 px-2"><span className="text-caption-1 text-tertiary tabular-nums">{a_9.selected.length + (a_9.otherSelected && a_9.text.trim() ? 1 : 0)} selected</span><Button size="compact" className="max-md:h-9!" disabled={!currentAnswered} onClick={advance}>{isLast ? "Submit" : "Next"}</Button></div>}</div>;
    };
    $[44] = advance;
    $[45] = allAnswered;
    $[46] = answers;
    $[47] = clearOther;
    $[48] = currentAnswered;
    $[49] = currentIndex;
    $[50] = goPrev;
    $[51] = handleImmediate;
    $[52] = handleSubmit;
    $[53] = hasAnyOptions;
    $[54] = immediate;
    $[55] = isLast;
    $[56] = paginated;
    $[57] = questions;
    $[58] = selectSingle;
    $[59] = send;
    $[60] = setText;
    $[61] = toggleMulti;
    $[62] = toggleOther;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const renderQuestion = t22;
  let t23;
  if ($[64] !== currentIndex || $[65] !== paginated || $[66] !== questions || $[67] !== renderQuestion) {
    t23 = paginated ? renderQuestion(questions[currentIndex], currentIndex) : questions.map((q_6, i_7) => renderQuestion(q_6, i_7));
    $[64] = currentIndex;
    $[65] = paginated;
    $[66] = questions;
    $[67] = renderQuestion;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== allAnswered || $[70] !== handleSubmit || $[71] !== hasAnyOptions || $[72] !== immediate || $[73] !== paginated) {
    t24 = !paginated && !immediate && hasAnyOptions && <div className="flex justify-end items-center border-t border-tertiary pt-3"><Button size="compact" className="max-md:h-9!" disabled={!allAnswered} onClick={handleSubmit}>Submit</Button></div>;
    $[69] = allAnswered;
    $[70] = handleSubmit;
    $[71] = hasAnyOptions;
    $[72] = immediate;
    $[73] = paginated;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== t23 || $[76] !== t24) {
    t25 = <PinnedCard ref={cardRef} className="pt-4 gap-4" shrinkable={true}>{t23}{t24}</PinnedCard>;
    $[75] = t23;
    $[76] = t24;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  return t25;
};
export default AskUserQuestionFlow;
function _temp(line) {
  const colonIdx = line.indexOf(": ");
  return colonIdx >= 0 ? line.slice(colonIdx + 2) : line;
}
function _temp2(a_4) {
  return a_4.otherSelected ? {
    ...a_4,
    otherSelected: false
  } : a_4;
}
function _temp3(q_1) {
  return !!q_1.options && q_1.options.length > 0;
}
function _temp4(a_6) {
  return a_6.otherSelected;
}
function _temp5(a_7) {
  return a_7.otherSelected ? {
    ...a_7,
    otherSelected: false
  } : a_7;
}
function _temp6(prev_1) {
  return prev_1.map(_temp5);
}
function _temp7(p) {
  return p.a;
}
function _temp8(p_0) {
  return <div key={p_0.q} className="flex flex-col text-primary"><span>Q: {p_0.q}</span><span>A: {p_0.a}</span></div>;
}
function _temp9(p_1) {
  return `Q: ${p_1.q}\nA: ${p_1.a}`;
}